/**
 * LibraryFilterChips — Filter als Chip-Zeile über dem Raster (Redesign S2
 * Phase 2a, 2026-10-10, `tasks/design-handoff-redesign-s2.md` Entscheid 4).
 * Ersetzt das rechte Filter-Panel + die Einzelwert-Leiste der ActiveFilters.
 *
 * Je Facette ein Chip (Typ · Produkt & Kategorie · Verwendung · Tags · Status)
 * mit Popover. Popover-Inhalt = die bestehende Sektion der `FilterBar` (`only`)
 * → dieselben Controls, Counts und Vokabular-Hooks wie im Mobile-Drawer, keine
 * zweite Filterlogik. Aktiv = grüner Chip mit Anzahl gewählter Werte + × (×
 * entfernt genau diese Facette, `stripChipFacet`). URL-State unverändert.
 *
 * Danach die Chips ohne Facette (Suche, Ohne Tags, Meine Uploads) und
 * „Alle Filter zurücksetzen" aus `ActiveFilters`.
 */
import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { I } from '../lib/icons';
import { useVocab } from '../lib/use-vocab';
import { STATUS_OPTIONS, TYPE_CHIPS } from '../lib/filter-options';
import type { ChipFacet } from '../lib/active-filters';
import { FilterBar, type FilterBarProps } from './FilterBar';
import { ActiveFilters } from './ActiveFilters';
import { Popover } from './ui/Popover';
import {
  FILTER_CHIP_ACTIVE,
  FILTER_CHIP_CLEAR,
  FILTER_CHIP_COUNT,
  FILTER_CHIP_INACTIVE,
} from './ui/filter-chip';

type FilterChipProps = {
  label: string;
  /** Anzahl gewählter Werte; 0 = inaktiv (kein ×, kein Badge). */
  count: number;
  /** Tooltip mit den gewählten Werten (Namen statt IDs). */
  title?: string;
  onClear: () => void;
  /** Popover-Inhalt. */
  children: ReactNode;
};

export function FilterChip({ label, count, title, onClear, children }: FilterChipProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const groupRef = useRef<HTMLSpanElement>(null);
  const popoverId = useId();
  const close = useCallback(() => setOpen(false), []);
  const active = count > 0;

  return (
    <span ref={groupRef} className="inline-flex shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-label={active ? `${label}, ${count} ausgewählt` : undefined}
        title={active ? title : undefined}
        onClick={() => setOpen((v) => !v)}
        className={active ? FILTER_CHIP_ACTIVE : FILTER_CHIP_INACTIVE}
      >
        {label}
        {active && (
          <span aria-hidden="true" className={FILTER_CHIP_COUNT}>
            {count}
          </span>
        )}
        <I.chevD
          size={14}
          aria-hidden="true"
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {/* Popover im DOM direkt hinter dem Trigger (fixed positioniert) →
          Tab führt vom Chip in den Popover-Inhalt, dann zum ×/nächsten Chip. */}
      <Popover
        open={open}
        onClose={close}
        anchorRef={triggerRef}
        insideRef={groupRef}
        label={label}
        id={popoverId}
      >
        {children}
      </Popover>
      {active && (
        <button
          type="button"
          aria-label={`${label}-Filter entfernen`}
          onClick={() => {
            onClear();
            // Das × verschwindet mit dem Filter → Fokus nicht ins Leere fallen lassen.
            triggerRef.current?.focus();
          }}
          className={FILTER_CHIP_CLEAR}
        >
          <I.close size={14} />
        </button>
      )}
    </span>
  );
}

type Props = {
  /** Dieselben Props wie das volle Panel (Mobile-Drawer) — Single-Source. */
  filters: Omit<FilterBarProps, 'only'>;
  /** × an einem Facetten-Chip: entfernt genau diese Facette. */
  onClearFacet: (facet: ChipFacet) => void;
  /** Chips ohne Facette + Reset (siehe ActiveFilters). */
  q: string;
  untagged: boolean;
  mine: boolean;
  anyActive: boolean;
  onClearQuery: () => void;
  onClearUntagged: () => void;
  onClearMine: () => void;
  onClearAll: () => void;
  /** Vorangestelltes Element in der Zeile (Mobile: „Filter"-Button für den Drawer). */
  leading?: ReactNode;
};

export function LibraryFilterChips({
  filters,
  onClearFacet,
  q,
  untagged,
  mine,
  anyActive,
  onClearQuery,
  onClearUntagged,
  onClearMine,
  onClearAll,
  leading,
}: Props) {
  const { type, status, kategorie, produkt, verwendung, tag } = filters;

  // ID→Name für die Chip-Tooltips (Fallback auf die ID, transient beim Laden).
  const { state } = useVocab();
  const vocab = state.status === 'ok' ? state.vocab : null;
  const kategorieName = (id: string): string =>
    vocab?.kategorien.find((k) => k.id === id)?.name ?? id;
  const produktName = (id: string): string =>
    vocab?.kategorien.flatMap((k) => k.produkte).find((p) => p.id === id)?.name ?? id;
  const verwendungName = (id: string): string =>
    vocab?.verwendungen.find((v) => v.id === id)?.name ?? id;

  const chips: { facet: ChipFacet; label: string; count: number; title: string }[] = [
    {
      facet: 'type',
      label: 'Typ',
      count: type !== 'all' ? 1 : 0,
      title: TYPE_CHIPS.find((c) => c.value === type)?.label ?? type,
    },
    {
      facet: 'taxonomy',
      label: 'Produkt & Kategorie',
      count: kategorie.length + produkt.length,
      title: [...kategorie.map(kategorieName), ...produkt.map(produktName)].join(', '),
    },
    {
      facet: 'verwendung',
      label: 'Verwendung',
      count: verwendung.length,
      title: verwendung.map(verwendungName).join(', '),
    },
    { facet: 'tag', label: 'Tags', count: tag.length, title: tag.join(', ') },
    {
      facet: 'status',
      label: 'Status',
      count: status !== 'approved' ? 1 : 0,
      title: STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status,
    },
  ];

  return (
    // Mobile: eine Zeile, horizontal scrollbar (kein Seiten-Overflow — der
    // Scroller ist min-w-0 im Flex-Parent). Ab md: umbrechen statt scrollen.
    // -m-1/p-1: Fokus-Ringe (ring-offset) nicht vom Scroller abschneiden lassen.
    <div
      role="group"
      aria-label="Filter-Leiste"
      className="min-w-0 flex-1 -m-1 p-1 overflow-x-auto md:overflow-visible"
      data-testid="filter-chip-row"
    >
      <div className="flex w-max md:w-auto items-center gap-2 md:flex-wrap">
        {leading}
        {chips.map((chip) => (
          <FilterChip
            key={chip.facet}
            label={chip.label}
            count={chip.count}
            title={chip.title}
            onClear={() => onClearFacet(chip.facet)}
          >
            <FilterBar {...filters} only={chip.facet} />
          </FilterChip>
        ))}
        <ActiveFilters
          q={q}
          untagged={untagged}
          mine={mine}
          anyActive={anyActive}
          onClearQuery={onClearQuery}
          onClearUntagged={onClearUntagged}
          onClearMine={onClearMine}
          onClearAll={onClearAll}
        />
      </div>
    </div>
  );
}
